import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme } from 'antd';
import { FastingLogDto, FastingPlanType, FastingStatus } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';

const statusColors: Record<FastingStatus, string> = {
  [FastingStatus.ACTIVE]: '#1890ff',
  [FastingStatus.COMPLETED]: '#3ecf8e',
  [FastingStatus.BROKEN]: '#ff6b6b',
};

const planTypeColors: Record<FastingPlanType, string> = {
  [FastingPlanType.SIXTEEN_EIGHT]: '#722ed1',
  [FastingPlanType.EIGHTEEN_SIX]: '#13c2c2',
  [FastingPlanType.TWENTY_FOUR]: '#fa8c16',
  [FastingPlanType.OMAD]: '#eb2f96',
  [FastingPlanType.CUSTOM]: '#8c8c8c',
};

export const FastingLogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<FastingLogDto>({ resource: 'fasting-logs' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const getDurationDisplay = () => {
    if (item.actualDurationMinutes !== undefined && item.actualDurationMinutes !== null) {
      const hours = Math.floor(item.actualDurationMinutes / 60);
      const mins = item.actualDurationMinutes % 60;
      return `${hours}h ${mins}m`;
    }
    if (item.status === FastingStatus.ACTIVE) {
      const now = Date.now();
      const startMs = new Date(item.startTime).getTime();
      const elapsed = Math.floor((now - startMs) / (60 * 1000));
      const hours = Math.floor(elapsed / 60);
      const mins = elapsed % 60;
      return `${hours}h ${mins}m (ongoing)`;
    }
    return `${item.targetDurationMinutes}m`;
  };

  return (
    <Show resource="fasting-logs">
      <div style={{ padding: '8px 0' }}>
        <Field label="Fasting Log ID">{item.id}</Field>
        <Field label="User ID">{item.userId}</Field>
        <Field label="Plan Type">
          <Tag
            style={{
              borderColor: planTypeColors[item.planType] || '#898989',
              color: planTypeColors[item.planType] || '#898989',
              background: `${planTypeColors[item.planType] || '#898989'}15`,
              borderRadius: 9999,
              padding: '2px 12px',
              fontWeight: 500,
            }}
          >
            {item.planType.replace('_', ' ')}
          </Tag>
        </Field>
        <Field label="Status">
          <Tag
            style={{
              borderColor: statusColors[item.status] || '#898989',
              color: statusColors[item.status] || '#898989',
              background: `${statusColors[item.status] || '#898989'}15`,
              borderRadius: 9999,
              padding: '2px 12px',
              fontWeight: 500,
            }}
          >
            {item.status}
          </Tag>
        </Field>
        <Field label="Start Time">{new Date(item.startTime).toLocaleString()}</Field>
        <Field label="Target End">{new Date(item.targetEnd).toLocaleString()}</Field>
        {item.endTime && <Field label="End Time">{new Date(item.endTime).toLocaleString()}</Field>}
        <Field label="Duration">{getDurationDisplay()}</Field>
        <Field label="Target Duration">{item.targetDurationMinutes} minutes</Field>
        {item.notes && <Field label="Notes">{item.notes}</Field>}
        {item.breakReason && (
          <Field label="Break Reason">
            <span style={{ color: '#ff6b6b' }}>{item.breakReason}</span>
          </Field>
        )}
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
        <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
      </div>
    </Show>
  );
};